import { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';

import PageLoading from '@components/page-loading';
import RouteErrorBoundary, {
  RootRouteErrorBoundary,
} from '@components/route-error-boundary';
import Scaffold from '@components/scaffold';
import { getAccountOptions } from '@lib/api/options/auth';
import AppError from '@lib/app-error';
import { findMatchingRoute } from '@lib/utils/route';
import {
  getAllModulesRoutes,
  getDefaultRoutePath,
  getModuleRoutesByAccount,
  isPublicRoutePath,
} from '@modules/index';
import { ROUTE_PATHS } from '@modules/route-paths';

import { getStoreRoutePath } from '@modules/stores/store-route';

import { isAuthenticationRoute } from '@modules/unauth/routes';

import { queryClient } from './query-client';
import { Account } from './types';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    const normalizedPath =
      location.pathname === '/'
        ? '/'
        : location.pathname.replace(/\/+$/, '') || '/';

    const isPublicPath = isPublicRoutePath(normalizedPath);
    const redirectTarget = `${normalizedPath}${location.searchStr ?? ''}${location.hash ? `#${location.hash.replace(/^#/, '')}` : ''}`;

    let account: Account | null | undefined;

    if (isPublicPath && !isAuthenticationRoute(normalizedPath)) {
      return;
    }

    try {
      account = await context.queryClient.ensureQueryData(getAccountOptions());
    } catch (error) {
      if (isPublicPath) {
        return;
      }

      if (error instanceof AppError) {
        if (error.isServiceUnavailable) {
          throw redirect({
            to: ROUTE_PATHS.SERVICE_UNAVAILABLE,
            search: { redirect: redirectTarget },
            replace: true,
          });
        }

        if (error.isUnauthorized) {
          throw redirect({
            to: ROUTE_PATHS.LOGIN,
            search: { redirect: redirectTarget },
            replace: true,
          });
        }
      }

      throw error;
    }

    if (!isPublicPath && !account) {
      throw redirect({
        to: ROUTE_PATHS.LOGIN,
        search: { redirect: redirectTarget },
        replace: true,
      });
    }

    if (
      isPublicPath &&
      isAuthenticationRoute(normalizedPath) &&
      account !== null
    ) {
      throw redirect({ to: getDefaultRoutePath(account), replace: true });
    }

    if (
      account?.forcePasswordChange &&
      normalizedPath !== ROUTE_PATHS.PROFILE
    ) {
      throw redirect({ to: ROUTE_PATHS.PROFILE, replace: true });
    }

    const allowedPaths = getModuleRoutesByAccount(account)
      .flatMap((route) => route.children ?? [route])
      .map((route) => route.path);

    const matchingRoute = findMatchingRoute(
      getAllModulesRoutes(),
      normalizedPath,
    );

    if (
      !matchingRoute ||
      !allowedPaths.includes(matchingRoute.path) ||
      (matchingRoute.acl &&
        (!account || !matchingRoute.acl.includes(account.role)))
    ) {
      throw redirect({ to: getDefaultRoutePath(account), replace: true });
    }
  },
  component: Scaffold,
  errorComponent: RootRouteErrorBoundary,
});

const routeChildren = getAllModulesRoutes()
  .flatMap((route) => (route.children ? route.children : [route]))
  .map((route) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path: route.path,
      component: route.page,
      beforeLoad: route.redirectTo
        ? ({ params, location }) => {
            const slug: unknown = Reflect.get(params, 'slug');
            const target =
              route.redirectTo === ROUTE_PATHS.STORE_FOLDER &&
              typeof slug === 'string'
                ? getStoreRoutePath(slug)
                : route.redirectTo;

            if (target && target !== route.path) {
              throw redirect({
                to: target,
                params,
                search: location.search,
                hash: location.hash,
                replace: true,
              });
            }
          }
        : undefined,
    }),
  );

export const router = createRouter({
  routeTree: rootRoute.addChildren(routeChildren),
  defaultPendingComponent: PageLoading,
  defaultErrorComponent: RouteErrorBoundary,
  defaultPendingMs: 0,
  defaultPendingMinMs: 0,
  context: { queryClient },
});

export type AppRouter = typeof router;
